/**
 * assignTimeSlots — pure function.
 *
 * Given a post's {platform, weekIndex, weekdayPool, hourPool}, assign a
 * concrete `scheduledAt` ISO timestamp + `weekdayIndex` (0=Mon..6=Sun)
 * that:
 *   - falls within the week's [startDate, endDate]
 *   - uses strategy/platform cadence when provided, otherwise a generic
 *     technical fallback
 *   - applies ±7-14 minute stochastic jitter to beat static-scheduler pileup
 *     (architecture doc: 12:00 / 15:00 exact minutes = server congestion)
 *
 * Deterministic seed — same (postId, weekIndex) → same timestamp. Makes
 * regeneration idempotent + tests reproducible.
 */

import type { PlatformId, StrategyPlatformCadence } from '../types';

// ─── Generic optimal window fallback ────────────────────────────────

export interface PlatformWindow {
  /** Preferred weekdays (0 = Mon, 6 = Sun) */
  weekdays: number[];
  /** Preferred hour bands (24h local time). Each band is [startHour, endHour]. */
  hourBands: Array<[number, number]>;
  /**
   * Optional exact day+time pairs. When present, assignTimeSlots picks from
   * these slots as pairs instead of crossing weekdays with hour bands.
   */
  slots?: Array<{ weekdayIndex: number; hourBand: [number, number]; minute?: number; exactTime?: boolean }>;
}

/**
 * Generic technical fallback used only when a strategy has no
 * platformCadences override. Product platform definitions live in
 * `system_platforms/*`; this fallback is intentionally not a platform
 * catalogue.
 */
export const DEFAULT_PLATFORM_WINDOW: PlatformWindow = {
  weekdays: [1, 2, 3],
  hourBands: [[9, 10]],
};

/**
 * Convert a user-configured StrategyPlatformCadence (HH:mm strings +
 * weekday list) into the internal PlatformWindow shape. Each time chip
 * becomes a 1-hour hourBand anchored at the chip's hour so the existing
 * LCG-based picker continues to work unchanged.
 */
function cadenceToWindow(cadence: StrategyPlatformCadence): PlatformWindow {
  const exactSlots = Array.isArray(cadence.slots)
    ? cadence.slots
        .map((slot) => {
          const [hh, mm] = String(slot.timeLocal ?? '').split(':').map(Number);
          const h = Number.isFinite(hh) ? Math.max(0, Math.min(23, hh)) : 12;
          const minute = Number.isFinite(mm) ? Math.max(0, Math.min(59, mm)) : 0;
          const weekdayIndex = Number(slot.weekdayIndex);
          if (!Number.isInteger(weekdayIndex) || weekdayIndex < 0 || weekdayIndex > 6) return null;
          return {
            weekdayIndex,
            hourBand: [h, h + 1] as [number, number],
            minute,
            exactTime: true,
          };
        })
        .filter((slot): slot is { weekdayIndex: number; hourBand: [number, number]; minute: number; exactTime: true } => Boolean(slot))
    : [];
  const preservedHourBands = Array.isArray(cadence.preferredHourBands)
    ? cadence.preferredHourBands
        .map((band) => {
          const raw = band as unknown as { startHour?: unknown; endHour?: unknown } | [unknown, unknown];
          const start = Number(Array.isArray(raw) ? raw[0] : raw.startHour);
          const end = Number(Array.isArray(raw) ? raw[1] : raw.endHour);
          if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
          return [
            Math.max(0, Math.min(23, start)),
            Math.max(0, Math.min(24, end)),
          ] as [number, number];
        })
        .filter((band): band is [number, number] => Boolean(band))
    : [];
  const hourBands: Array<[number, number]> = cadence.preferredTimesLocal.map((t) => {
    const [hh] = t.split(':').map(Number);
    const h = Number.isFinite(hh) ? hh : 12;
    return [h, h + 1] as [number, number];
  });
  const effectiveHourBands = exactSlots.length > 0
    ? exactSlots.map((slot) => slot.hourBand)
    : preservedHourBands.length > 0
      ? preservedHourBands
      : hourBands;
  const effectiveWeekdays = exactSlots.length > 0
    ? Array.from(new Set(exactSlots.map((slot) => slot.weekdayIndex))).sort()
    : [...cadence.preferredWeekdays].sort();
  return {
    weekdays: effectiveWeekdays,
    hourBands: effectiveHourBands.length > 0 ? effectiveHourBands : [[9, 10]],
    ...(exactSlots.length > 0 ? { slots: exactSlots } : {}),
  };
}

/**
 * Resolve the effective window for a given platform, given an optional
 * per-strategy cadence list. Callers that don't know about cadence can
 * still pass `undefined` and get a generic defensive default.
 */
export function resolveCadenceWindows(
  platform: PlatformId,
  cadences?: StrategyPlatformCadence[] | null,
): PlatformWindow {
  if (cadences && cadences.length > 0) {
    const match = cadences.find((c) => c.platform === platform);
    if (match) return cadenceToWindow(match);
  }
  return DEFAULT_PLATFORM_WINDOW;
}

// ─── Seeded pseudo-random (LCG) — deterministic per-post ─────────────

function lcg(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function hashStringToSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

// ─── Main ─────────────────────────────────────────────────────────────

export interface AssignTimeSlotInput {
  postId: string;                  // used as deterministic seed
  platform: PlatformId;
  weekStartDate: string;           // ISO YYYY-MM-DD (Mon)
  weekEndDate: string;             // ISO YYYY-MM-DD (Sun)
  timezone?: string;               // IANA — currently unused (we emit UTC ISO)
  /**
   * Optional per-strategy cadence override. When provided and contains a
   * match for `platform`, replaces the generic default fallback.
   * When absent, the generic default window is used.
   */
  cadences?: StrategyPlatformCadence[] | null;
}

export interface AssignTimeSlotOutput {
  weekdayIndex: number;            // 0=Mon..6=Sun
  scheduledAt: string;             // ISO timestamp
  hourBandUsed: [number, number];
  jitterMinutes: number;           // ±7..14
}

export function assignTimeSlots(input: AssignTimeSlotInput): AssignTimeSlotOutput {
  const window = resolveCadenceWindows(input.platform, input.cadences);
  if (!window) {
    throw new Error(`assignTimeSlots: no optimal window for platform "${input.platform}"`);
  }

  const rand = lcg(hashStringToSeed(input.postId));

  // Pick weekday from platform's preferred set, constrained to within the week window
  const weekStart = new Date(`${input.weekStartDate}T00:00:00.000Z`);
  const weekEnd = new Date(`${input.weekEndDate}T00:00:00.000Z`);
  const maxDayOffset = Math.floor((weekEnd.getTime() - weekStart.getTime()) / 86_400_000);
  const eligibleSlots = (window.slots ?? []).filter((slot) => slot.weekdayIndex <= maxDayOffset);
  let pickedDay: number;
  let band: [number, number];
  let pickedMinute: number | undefined;
  let exactTime = false;
  if (eligibleSlots.length > 0) {
    const slot = eligibleSlots[Math.floor(rand() * eligibleSlots.length)];
    pickedDay = slot.weekdayIndex;
    band = slot.hourBand;
    pickedMinute = slot.minute;
    exactTime = slot.exactTime === true;
  } else {
    const eligibleDays = window.weekdays.filter((d) => d <= maxDayOffset);
    pickedDay = eligibleDays.length > 0
      ? eligibleDays[Math.floor(rand() * eligibleDays.length)]
      : Math.floor(rand() * (maxDayOffset + 1));
    band = window.hourBands[Math.floor(rand() * window.hourBands.length)];
  }

  // Pick hour within band
  const [bandStart, bandEnd] = band;
  const hour = exactTime
    ? bandStart
    : bandStart + Math.floor(rand() * Math.max(1, bandEnd - bandStart + 1));

  // Exact user-configured HH:mm slots must remain exact. Generic hour-band
  // fallback keeps deterministic jitter to avoid round-number pileups.
  const jitterMagnitude = exactTime ? 0 : 7 + Math.floor(rand() * 8); // 7..14
  const jitterSign = exactTime ? 1 : rand() < 0.5 ? -1 : 1;
  const jitterMinutes = jitterSign * jitterMagnitude;

  const scheduled = new Date(weekStart);
  scheduled.setUTCDate(weekStart.getUTCDate() + pickedDay);
  scheduled.setUTCHours(hour, (pickedMinute ?? 0) + jitterMinutes, 0, 0);

  return {
    weekdayIndex: pickedDay,
    scheduledAt: scheduled.toISOString(),
    hourBandUsed: band,
    jitterMinutes,
  };
}
